// ============================================================================
// Prescription math — parse human-written dose/frequency/duration into
// machine-usable numbers, and compute the total quantity needed.
//
// Rules of the game (conservative by design — return null rather than
// guess on anything ambiguous, so the UI can show "add manually"):
//
//   parseAmount("500 mg")   -> { value: 500,  unit: "mg" }
//   parseAmount("1 g")      -> { value: 1000, unit: "mg" }   // normalized to mg
//   parseAmount("5 mL")     -> { value: 5,    unit: "ml" }
//   parseFrequency("qid")   -> 4
//   parseFrequency("bd")    -> 2
//   parseFrequency("q8h")   -> 3
//   parseDuration("5 days") -> 5
//   parseDuration("1 week") -> 7
//   parseDuration("10/7")   -> 10
// ============================================================================

// ---- unit normalisation ----------------------------------------------------
// Base units: mg for mass, ml for volume, unit for units-based meds,
// tab/cap for pre-counted forms.
//
// Each map covers BOTH the abbreviation (g, mg, ml) AND the spelled-out
// word (gram, milligram, millilitre) since clinicians legitimately write
// "1 gram" just as often as "1 g". After lowercasing and stripping a
// trailing 's', both "1 gram" and "1 grams" land on the key "gram".

const MASS_TO_MG = {
  mcg: 0.001, ug: 0.001, microgram: 0.001,
  mg: 1, milligram: 1,
  g: 1000, gm: 1000, gram: 1000,
  kg: 1_000_000, kilogram: 1_000_000,
}

const VOLUME_TO_ML = {
  ml: 1, milliliter: 1, millilitre: 1,
  l: 1000, liter: 1000, litre: 1000,
}

const COUNT_FORMS = ['tab', 'tablet', 'cap', 'capsule', 'pill']

const VERBATIM_FORMS = ['drop', 'gtt', 'puff', 'spray', 'sachet', 'supp', 'pessary']

export function parseAmount(s) {
  if (s == null) return null
  const text = String(s).trim().toLowerCase()
  if (!text) return null
  // Handles "500 mg", "500mg", "1 g", "1 gram", "5 ml", "5 mls",
  // "2 tabs", "10 iu". Greedy letter run captures the full word, so
  // "milligram" / "millilitre" / "kilogram" all parse cleanly.
  const m = text.match(/([\d.]+)\s*([a-zµ]+)?/)
  if (!m) return null
  const value = Number(m[1])
  if (!Number.isFinite(value)) return null
  // Strip a trailing 's' so plurals collapse: "tabs" → "tab", "grams" →
  // "gram", "mls" → "ml", "milligrams" → "milligram". Defensible because
  // none of our canonical keys end in 's'.
  const rawUnit = (m[2] || '').replace(/s$/, '')
  if (!rawUnit) return { value, unit: '' }

  // Normalise mass → mg
  if (rawUnit in MASS_TO_MG) {
    return { value: value * MASS_TO_MG[rawUnit], unit: 'mg' }
  }
  // Volume → ml
  if (rawUnit in VOLUME_TO_ML) {
    return { value: value * VOLUME_TO_ML[rawUnit], unit: 'ml' }
  }
  if (['iu', 'u', 'unit'].includes(rawUnit)) {
    return { value, unit: 'unit' }
  }
  if (COUNT_FORMS.includes(rawUnit)) {
    return { value, unit: 'tab' }
  }
  if (VERBATIM_FORMS.includes(rawUnit)) {
    return { value, unit: rawUnit }
  }
  return { value, unit: rawUnit }  // pass through unknown units
}

// ---- frequency parsing -----------------------------------------------------
// Returns "doses per day" as a number, or null if we can't tell.

const FREQ_MAP = {
  od: 1, daily: 1, 'once daily': 1, 'once a day': 1, qd: 1,
  nocte: 1, mane: 1, 'at night': 1, 'at bedtime': 1, bedtime: 1,
  hs: 1,
  bd: 2, bid: 2, 'twice daily': 2, 'twice a day': 2, 'b.d': 2, 'b.i.d': 2,
  tds: 3, tid: 3, 'thrice daily': 3, 'three times daily': 3, 'three times a day': 3, 't.d.s': 3,
  qid: 4, qds: 4, 'four times daily': 4, 'four times a day': 4, 'q.d.s': 4, 'q.i.d': 4,
  '5/day': 5,
  // q<N>h → 24/N
  stat: 1,
  prn: null,  // as-needed — can't auto-calc total
}

export function parseFrequency(s) {
  if (s == null) return null
  const text = String(s).trim().toLowerCase().replace(/\s+/g, ' ')
  if (!text) return null
  if (text in FREQ_MAP) return FREQ_MAP[text]

  // q4h, q6h, q8h, q12h, etc.
  const qh = text.match(/^q\s*([\d.]+)\s*h(?:rs?|ours?)?$/)
  if (qh) {
    const hours = Number(qh[1])
    if (hours > 0) return 24 / hours
  }

  // "x times/day", "x times per day", "x x/day"
  const tpd = text.match(/^([\d]+)\s*(?:x|times?)\s*(?:\/|per|a)?\s*day$/)
  if (tpd) return Number(tpd[1])

  // Try individual tokens
  for (const [k, v] of Object.entries(FREQ_MAP)) {
    if (v != null && text === k) return v
  }
  return null
}

// ---- duration parsing ------------------------------------------------------
// Returns "total days" as a number, or null if unclear.

export function parseDuration(s) {
  if (s == null) return null
  const text = String(s).trim().toLowerCase()
  if (!text) return null

  // PNG/UK shorthand: "10/7" = 10 days, "3/52" = 3 weeks, "6/12" = 6 months.
  const shortForm = text.match(/^([\d.]+)\s*\/\s*(7|52|12)$/)
  if (shortForm) {
    const n = Number(shortForm[1])
    const denom = Number(shortForm[2])
    if (denom === 7) return n
    if (denom === 52) return n * 7
    if (denom === 12) return n * 30
  }

  const m = text.match(/^([\d.]+)\s*(d|day|days|w|week|weeks|wk|wks|m|month|months|mon|y|year|years|yr|yrs)?\.?$/)
  if (!m) return null
  const n = Number(m[1])
  if (!Number.isFinite(n)) return null
  const u = m[2] || 'd'
  if (/^(d|day|days)$/.test(u)) return n
  if (/^(w|wk|wks|week|weeks)$/.test(u)) return n * 7
  if (/^(m|mon|month|months)$/.test(u)) return n * 30
  if (/^(y|yr|yrs|year|years)$/.test(u)) return n * 365
  return n
}

// ---- quantity calculator ---------------------------------------------------
/**
 * Compute the total quantity to dispense.
 *
 * @param {object} params
 * @param {string} params.dose       e.g. "1 g" (per-dose amount)
 * @param {string} params.strength   e.g. "500 mg" (per-tablet/ampoule strength)
 * @param {string} params.frequency  e.g. "qid"
 * @param {string} params.duration   e.g. "5 days"
 * @returns {{ total: number, unit: string, perDose: number, perDay: number, days: number } | null}
 *          Null when any input can't be parsed unambiguously.
 */
export function calculateQuantity({ dose, strength, frequency, duration }) {
  const d = parseAmount(dose)
  const s = parseAmount(strength)
  const freq = parseFrequency(frequency)
  const days = parseDuration(duration)

  if (!d || freq == null || days == null) return null

  // Strength-based math (tabs/capsules/ampoules): we need matching units.
  if (s && s.unit && d.unit && s.unit === d.unit && s.value > 0) {
    const perDose = d.value / s.value
    const total = perDose * freq * days
    return {
      perDose, perDay: perDose * freq, days, total,
      unit: d.unit === 'ml' ? 'ml' : 'tabs',
    }
  }

  // Liquid without a matching strength — express total in the dose's unit.
  if (d.unit === 'ml' && (!s || !s.unit)) {
    return { perDose: d.value, perDay: d.value * freq, days, total: d.value * freq * days, unit: 'ml' }
  }

  // Fallback for when the doctor prescribed in "tabs" directly.
  if (d.unit === 'tab') {
    return { perDose: d.value, perDay: d.value * freq, days, total: d.value * freq * days, unit: 'tabs' }
  }

  return null
}

/**
 * Format a calculator result as a short, doctor-readable hint.
 *   "2 tabs × 4/day × 5 days = 40 tabs"
 */
export function formatQuantityHint(result) {
  if (!result) return ''
  const { perDose, perDay, days, total, unit } = result
  const round = (n) => Math.round(n * 100) / 100
  return `${round(perDose)} ${unit} × ${round(perDay / perDose || perDose)}/day × ${round(days)} days = ${round(total)} ${unit}`
}

/** Round up to an integer if the result is tab/capsule-like. */
export function totalForDispensing(result) {
  if (!result) return null
  if (result.unit === 'tabs') return Math.ceil(result.total)
  return Math.round(result.total * 100) / 100
}
